/**
 * SchoolUserSignIn.js
 *
 * Password sign-in page for RMS school representatives (SENCOs) registered by
 * a provider. A SENCO manages several children's details, so they sign in
 * with a password; parents and carers keep the emailed sign-in link on
 * /rms/family/sign-in (design decision, 6 Oct 2026). Class teachers sign in
 * here with a password too (TDI-619) and land on their pupils (/rms/teacher).
 *
 * Built to the "Sign in · everyone" designs (Figma UAXOnhR1ZvlsNl4diy00xX,
 * frame 3954:255649), so the composition matches FamilySignInPage.
 *
 * Flow:
 *   - Staff land here from the password-setup page (with a success banner and
 *     their email pre-filled), from their bookmark, or from a protected page
 *     that bounced them with ?continue=.
 *   - They sign in with email + password via Firebase Auth.
 *   - A wrong-account guard signs out any account that is not an RMS school
 *     account (e.g. a provider account) and points it to the right portal.
 *   - On success: back to ?continue=, or the RMS service-user dashboard,
 *     which routes school users onward.
 *   - "Forgotten your password?" swaps the sign-in card for an on-page
 *     reset card, then a "Check your email" card (TDI-702, frames
 *     3954:255685 and 3954:255701). The server answers the same for every
 *     address, so the cards never reveal whether an account exists.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { apiSendSchoolUserPasswordReset } from '../utils/rmsApi';
import { getOrgConfig } from '../utils/orgResolution';
import useRmsAnalytics from '../hooks/useRmsAnalytics';
import HarbourBrandLockup from '../components/harbour/HarbourBrandLockup';
import HarbourButton from '../components/harbour/HarbourButton';
import { HarbourField } from '../components/harbour/HarbourField';
import HarbourNotice from '../components/harbour/HarbourNotice';
import HarbourTextLink from '../components/harbour/HarbourTextLink';
import { SIGNED_OUT_NOTICE } from '../constants/signedOutNotice';
import {
  RESET_NOTHING_ARRIVED,
  RESET_SEND_ANOTHER,
  RESET_SEND_FAILED,
  RESET_SENT_TITLE,
  resetSentBody,
} from '../constants/passwordReset';
import '../components/harbour/harbour.css';
import './schoolSignIn.css';

const DEFAULT_CONTINUE = '/rms/dashboard';
const TEACHER_HOME = '/rms/teacher';

// Copy from the "SENCO sign in" design frames; implemented verbatim. The
// reset cards' wording is TDI-702's (Diana updates the frame afterwards).
const COPY = {
  title: 'Sign in',
  emailLabel: 'Your email address',
  passwordLabel: 'Password',
  resetLink: 'Forgotten your password? We can email a reset link',
  signIn: 'Sign in',
  // TDI-619: shown when the page was opened for a teacher (from a teacher's
  // email or their signed-out pupils page), so a SENCO is never told to
  // contact the SENCO.
  teacherHelp: 'Need help signing in? Contact the SENCO at your school.',
  resetTitle: 'Reset your password',
  resetIntro: 'One link, and you are back in.',
  resetHint: 'The address your invitation email came to.',
  resetSubmit: 'Email me a reset link',
  wrongAddress: 'Used the wrong address? Go back and change it',
  backToSignIn: 'Back to sign in',
  // The family sign-in page's format error, so both pages say it alike.
  emailFormatError: 'Enter an email address in the correct format, like name@example.com',
};

export default function SchoolUserSignIn() {
  const { orgId } = useMemo(() => getOrgConfig(), []);
  const analytics = useRmsAnalytics(orgId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  // view: 'signin' | 'reset' (ask for a reset link) | 'resetSent' (check your email)
  const [view, setView] = useState('signin');
  const [resetEmail, setResetEmail] = useState('');
  const [resetSentTo, setResetSentTo] = useState('');
  const [resetFieldError, setResetFieldError] = useState('');
  const [resetSendError, setResetSendError] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  // Each card swap moves focus to the new card's heading, so a screen reader
  // hears the one message once. Not on first load: focus stays where the
  // browser puts it.
  const headingRef = useRef(null);
  const viewChanged = useRef(false);
  useEffect(() => {
    if (viewChanged.current && headingRef.current) headingRef.current.focus();
  }, [view]);
  const goTo = (next) => {
    viewChanged.current = true;
    setView(next);
  };

  const signedOut = searchParams.get('signedout') === '1';
  const requestedContinue = searchParams.get('continue');
  const continuePath = requestedContinue || DEFAULT_CONTINUE;
  const forTeacher = Boolean(requestedContinue && requestedContinue.startsWith(TEACHER_HOME));

  const isValidEmail = (emailStr) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailStr);
  };

  // Pre-fill the email and show a banner when arriving from password setup.
  useEffect(() => {
    const message = searchParams.get('message');
    if (message === 'setup_complete') {
      setInfoMessage('Your password is set. Please sign in to continue.');
    } else if (message === 'account_already_setup') {
      setInfoMessage('Your account is already set up. Please sign in with your password.');
    }

    const encodedEmail = searchParams.get('e');
    if (encodedEmail) {
      try {
        const decoded = atob(decodeURIComponent(encodedEmail));
        if (isValidEmail(decoded)) setEmail(decoded);
      } catch (_) {
        // Ignore a malformed email param; the user can type their email.
      }
    }
  }, [searchParams]);

  // Wrong-account guard: this page is for school accounts only (SENCOs and
  // class teachers). A provider account would be dropped into service-user
  // flows it was never part of, and a parent/carer account belongs on the
  // email-link page. Resolves to the account's user type, or null.
  const ensureSchoolUserAccount = async () => {
    let claims = null;
    try {
      const tokenResult = await auth.currentUser?.getIdTokenResult();
      claims = tokenResult?.claims || null;
    } catch (_) {
      // Unreadable token -> treat as not a school account.
    }
    if (
      claims?.accountType === 'RMS Service User' &&
      (claims?.userType === 'RMS School Representative' || claims?.userType === 'RMS Teacher')
    ) {
      return claims.userType;
    }
    try {
      await signOut(auth);
    } catch (_) {}
    if (claims?.accountType === 'RMS Provider User') {
      setError('This sign-in page is for school staff. Please use the provider sign-in page instead.');
    } else if (claims?.accountType === 'RMS Service User') {
      setError('This sign-in page is for school staff. Please sign in with an email link instead.');
    } else {
      setError('Incorrect account for this portal. Please check you are signing in with your school account.');
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      const userType = await ensureSchoolUserAccount();
      if (!userType) return;
      // A teacher with nowhere particular to go lands on their pupils.
      navigate(userType === 'RMS Teacher' && !requestedContinue ? TEACHER_HOME : continuePath);
    } catch (err) {
      const code = err?.code || '';
      if (
        code === 'auth/invalid-credential' ||
        code === 'auth/wrong-password' ||
        code === 'auth/user-not-found'
      ) {
        setError('Incorrect email or password. Please try again.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else if (code === 'auth/user-disabled') {
        setError('This account has been disabled. Please contact the referral service.');
      } else {
        setError('Could not sign you in. Please try again.');
      }
      analytics.error({
        where: 'schoolUserSignIn.submit',
        code: code || 'unknown',
        message: String(err?.message || err),
      });
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = isValidEmail(email) && !!password && !busy;

  const openReset = () => {
    setResetEmail(email.trim());
    setResetFieldError('');
    setResetSendError('');
    goTo('reset');
  };

  // Every press sends a fresh link (the server replaces the earlier one).
  const sendReset = async (address) => {
    const trimmed = String(address || '').trim();
    if (!isValidEmail(trimmed)) {
      setResetFieldError(COPY.emailFormatError);
      return;
    }
    setResetFieldError('');
    setResetSendError('');
    setResetBusy(true);
    try {
      await apiSendSchoolUserPasswordReset({ email: trimmed });
      setResetSentTo(trimmed);
      if (view !== 'resetSent') goTo('resetSent');
    } catch (err) {
      setResetSendError(RESET_SEND_FAILED);
      analytics.error({
        where: 'schoolUserSignIn.reset',
        code: err?.code || 'unknown',
        message: String(err?.message || err),
      });
    } finally {
      setResetBusy(false);
    }
  };

  const handleResetSubmit = (e) => {
    e.preventDefault();
    if (resetBusy) return;
    sendReset(resetEmail);
  };

  // Back to the request card with the address filled in, to correct it.
  const changeResetAddress = () => {
    setResetEmail(resetSentTo);
    setResetSendError('');
    goTo('reset');
  };

  const backToSignIn = () => {
    const known = resetSentTo || resetEmail;
    if (!email && known) setEmail(known);
    setResetSendError('');
    goTo('signin');
  };

  return (
    <div className="harbour harbour-page school-signin">
      <header className="school-signin-top-bar">
        <HarbourBrandLockup />
      </header>
      <main className="school-signin-main">
        {view === 'reset' && (
          <div className="school-signin-card">
            <div className="school-signin-head">
              <h1 className="school-signin-title" ref={headingRef} tabIndex={-1}>{COPY.resetTitle}</h1>
              <p className="school-signin-intro">{COPY.resetIntro}</p>
            </div>

            {!!resetSendError && <HarbourNotice kind="error" title={resetSendError} />}

            <form className="school-signin-form" onSubmit={handleResetSubmit} noValidate>
              <HarbourField
                className="school-signin-field"
                label={COPY.emailLabel}
                hint={COPY.resetHint}
                type="email"
                name="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                error={resetFieldError}
                disabled={resetBusy}
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="send"
              />
              <div className="school-signin-actions school-signin-actions--split">
                <HarbourButton
                  kind="primary"
                  type="submit"
                  busy={resetBusy}
                  aria-busy={resetBusy || undefined}
                >
                  {COPY.resetSubmit}
                </HarbourButton>
                <HarbourTextLink className="school-signin-alt-link" onClick={backToSignIn}>
                  {COPY.backToSignIn}
                </HarbourTextLink>
              </div>
            </form>
          </div>
        )}

        {view === 'resetSent' && (
          <div className="school-signin-card">
            <div className="school-signin-head">
              <h1 className="school-signin-title" ref={headingRef} tabIndex={-1}>{RESET_SENT_TITLE}</h1>
              <p className="school-signin-intro school-signin-sent-body">{resetSentBody(resetSentTo)}</p>
            </div>
            <p className="school-signin-intro">{RESET_NOTHING_ARRIVED}</p>

            {!!resetSendError && <HarbourNotice kind="error" title={resetSendError} />}

            <div className="school-signin-actions school-signin-actions--split">
              <HarbourButton
                kind="primary"
                onClick={() => sendReset(resetSentTo)}
                busy={resetBusy}
                aria-busy={resetBusy || undefined}
              >
                {RESET_SEND_ANOTHER}
              </HarbourButton>
              <HarbourTextLink className="school-signin-alt-link" onClick={changeResetAddress}>
                {COPY.wrongAddress}
              </HarbourTextLink>
            </div>
            <HarbourTextLink className="school-signin-back" onClick={backToSignIn}>
              {COPY.backToSignIn}
            </HarbourTextLink>
          </div>
        )}

        {view === 'signin' && (
          <div className="school-signin-card">
            <h1 className="school-signin-title" ref={headingRef} tabIndex={-1}>{COPY.title}</h1>

            {signedOut && <HarbourNotice kind="success" title={SIGNED_OUT_NOTICE} />}

            {!!infoMessage && <HarbourNotice kind="success" title={infoMessage} />}

            {!!error && <HarbourNotice kind="error" title={error} />}

            <form className="school-signin-form" onSubmit={handleSubmit} noValidate>
              <HarbourField
                className="school-signin-field"
                label={COPY.emailLabel}
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
              />
              <HarbourField
                className="school-signin-field"
                label={COPY.passwordLabel}
                name="password"
                reveal
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                autoComplete="current-password"
                enterKeyHint="go"
              />
              <HarbourTextLink
                className="school-signin-reset"
                onClick={openReset}
              >
                {COPY.resetLink}
              </HarbourTextLink>
              <div className="school-signin-actions">
                <HarbourButton
                  kind="primary"
                  type="submit"
                  disabled={!canSubmit}
                  busy={busy}
                  aria-busy={busy || undefined}
                >
                  {COPY.signIn}
                </HarbourButton>
              </div>
            </form>
          </div>
        )}

        {forTeacher && (
          <aside className="school-signin-help">
            <HarbourNotice kind="info">{COPY.teacherHelp}</HarbourNotice>
          </aside>
        )}

        <nav className="school-signin-footer-links" aria-label="Portal links">
          {/* Destinations are not defined yet in TDI-53; the row is in every
              sign-in design, so it ships with placeholder targets. */}
          <a href="#help">Help</a>
          <span aria-hidden="true"> · </span>
          <a href="#privacy">Privacy</a>
          <span aria-hidden="true"> · </span>
          <a href="#accessibility">Accessibility</a>
          <span aria-hidden="true"> · </span>
          <a href="#feedback">Feedback</a>
        </nav>
      </main>
    </div>
  );
}
